import {
    Component,
    h,
    Host,
    Prop,
    State,
    Element,
    Watch,
    Listen,
} from "@stencil/core";
import { formatNumber } from "../../utils/formatNumber";

const numberRegex = /([\d,.]+)/;
const prefixRegex = /(([^\d,.]+))*(?=[\d,.]+)/;

/**
 * @slot (default) - for the title and the subtitle of the teaser
 */

@Component({
    tag: "ux-iws-teaser-counter",
    styleUrl: "teaser-counter.scss",
    shadow: false,
})
export class TeaserCounter {
    private baseClass = "ux-iws-teaser-counter";

    @Element() element: HTMLUxIwsTeaserCounterElement;

    /** A String that may contain prefix and suffix. This field is needed because we have a lot of old content in the CMS that has no clean separation between number and prefix/suffix */
    @Prop() number: string = null;
    /** The suffix to add at the end of the number */
    @Prop() suffixValue: string = "";
    /** The suffix to add before the number */
    @Prop() prefixValue: string = "";
    /** The separator for the decimals, like "." or "," */
    @Prop() decimalSeparator: string = ".";
    /** the number of decimals to be displayed */
    @Prop() decimalsCount: number = 2;
    /** The separator for the thousands, like "." or "," */
    @Prop() thousandsSeparator: string = ",";
    /** if set, the number will not have the animation effect */
    @Prop() noAnimation: boolean = false;

    @State() isTeaserInView: boolean = false;
    @State() progress: number = 0;

    @Watch("isTeaserInView")
    animateValue() {
        let startTimestamp = null;
        const step = (timestamp: number): void => {
            if (!startTimestamp) startTimestamp = timestamp;
            this.progress = Math.min((timestamp - startTimestamp) / 2000, 1);
            if (this.progress < 1) {
                window.requestAnimationFrame(step);
            }
        };
        window.requestAnimationFrame(step);
    }

    private isInView = () => {
        if (this.isTeaserInView === true) return;
        const rects = this.element.getBoundingClientRect();
        this.isTeaserInView =
            rects.top < window.innerHeight && rects.bottom >= 0;
    };

    componentDidLoad() {
        if (this.noAnimation) return;
        this.isInView();
    }

    @Listen("scroll", { target: "window" })
    handleScroll() {
        if (this.noAnimation) return;
        this.isInView();
    }

    private getNumber(): number {
        const matches = this.number.match(numberRegex);
        return matches === null ? 0 : Number(matches[0]);
    }

    private getPrefix(): string {
        let prefix = this.prefixValue;
        const matches = this.number.match(prefixRegex);
        if (matches !== null) {
            prefix += matches[0];
        }
        return prefix;
    }

    private getSuffix(): string {
        let suffix = "";
        suffix = this.number;
        suffix = suffix.replace(prefixRegex, "");
        suffix = suffix.replace(numberRegex, "");
        suffix += this.suffixValue;
        return suffix;
    }

    render() {
        const number = this.getNumber();
        const stringifiedNumber = (this.progress * number).toFixed(
            Number(this.decimalsCount)
        );
        const formattedNumber = formatNumber({
            stringifiedNumber: this.noAnimation
                ? String(number)
                : stringifiedNumber,
            decimalSeparator: this.decimalSeparator,
            thousandsSeparator: this.thousandsSeparator,
        });

        return (
            <Host class={this.baseClass}>
                <div class={`${this.baseClass}__number`}>
                    <span>{this.getPrefix()}</span>
                    <span>{formattedNumber}</span>
                    <span>{this.getSuffix()}</span>
                </div>
                <hr class={`${this.baseClass}__separator`} />
                <slot />
            </Host>
        );
    }
}
